import { ChangeDetectionStrategy, Component, computed, input, output } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { MatTooltip } from '@angular/material/tooltip';
import { connectState } from '@examdojo/angular/util';
import { selectFormRawValue } from '@examdojo/core/form';
import { ContentItemTypeEnum, ImageContentItemFormGroup } from '@examdojo/models/content-item';
import { QuestionEditingOptions } from '@examdojo/models/question-editor';
import {
  ContentItemFormGroup,
  ContentItemViewerComponent,
  TableContentItemFormGroup,
} from '@examdojo/question-content-item';
import { SelectorBadgeComponent } from '@examdojo/ui/selector-badge';
import { switchMap } from 'rxjs';
import { MarkschemeNoteComponent } from '../../viewer/markscheme-note/markscheme-note.component';
import {
  MARKSCHEME_ITEM_TYPE_TO_COLOR,
  MARKSCHEME_ITEM_TYPE_TO_LONG_LABEL,
  MARKSCHEME_ITEM_TYPE_TO_SHORT_LABEL,
  MarkschemeEditorItemEnum,
} from '../markscheme-item.model';
import { MarkGroupFormGroup } from './markscheme-markgroup-editor.model';

@Component({
  selector: 'dojo-markscheme-markgroup-form-viewer',
  templateUrl: './markscheme-markgroup-form-viewer.component.html',
  imports: [ContentItemViewerComponent, SelectorBadgeComponent, MarkschemeNoteComponent, MatTooltip],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MarkschemeMarkgroupFormViewerComponent {
  readonly formCtrl = input.required<MarkGroupFormGroup>();
  readonly isActive = input.required<boolean>();
  readonly disabled = input.required<boolean>();
  readonly isAllowedToEditImage = input.required<boolean>();
  readonly editingOptions = input.required<QuestionEditingOptions>();

  readonly isAllowedToEditTable = computed(
    () => this.isAllowedToEditImage() || this.editingOptions().allowMarkschemeEditing,
  );

  readonly setActive = output<void>();
  readonly setActiveAndEditItem = output<{
    markGroup: MarkGroupFormGroup;
    contentItem: ImageContentItemFormGroup | TableContentItemFormGroup;
    criterionIndex: number;
  }>();

  readonly MARKSCHEME_ITEM_TYPE_TO_COLOR = MARKSCHEME_ITEM_TYPE_TO_COLOR;
  readonly MARKSCHEME_ITEM_TYPE_TO_SHORT_LABEL = MARKSCHEME_ITEM_TYPE_TO_SHORT_LABEL;
  readonly MARKSCHEME_ITEM_TYPE_TO_LONG_LABEL = MARKSCHEME_ITEM_TYPE_TO_LONG_LABEL;

  readonly MarkschemeEditorItemEnum = MarkschemeEditorItemEnum;

  state = connectState({
    formCtrlValue: toObservable(this.formCtrl).pipe(switchMap((form) => selectFormRawValue(form))),
  });

  handleEditItem({ item, contentItemIndex }: { item: ContentItemFormGroup; contentItemIndex: number }) {
    // Handle both image and table content items
    if (item.value.type === ContentItemTypeEnum.Image) {
      this.setActiveAndEditItem.emit({
        markGroup: this.formCtrl(),
        contentItem: item as ImageContentItemFormGroup,
        criterionIndex: contentItemIndex,
      });
    } else if (item.value.type === ContentItemTypeEnum.Table) {
      this.setActiveAndEditItem.emit({
        markGroup: this.formCtrl(),
        contentItem: item as TableContentItemFormGroup,
        criterionIndex: contentItemIndex,
      });
    }
  }
}
